import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import AssignGroupChildrenGroupsInteractor from "../interactors/AssignGroupChildrenGroupsInteractor";
import AssignGroupItemsInteractor from "../interactors/AssignGroupItemsInteractor";
import CreateIndexTypeGroupInteractor from "../interactors/CreateIndexTypeGroupInteractor";
import CreateIndexTypeInteractor from "../interactors/CreateIndexTypeInteractor";
import DeleteIndexGroupTypeInteractor from "../interactors/DeleteIndexGroupTypeInteractor";
import GetAllGroupChildGroupsInteractor from "../interactors/GetAllGroupChildGroupsInteractor";
import GetAllGroupItemsInteractor from "../interactors/GetAllGroupItemsInteractor";
import GetAllIndexTypeGroupsForCategoryInteractor from "../interactors/GetAllIndexTypeGroupsForCategory";
import GetAllIndexTypesInteractor from "../interactors/GetAllIndexTypesInteractor";
import UpdateIndexGroupTypeInteractor from "../interactors/UpdateIndexGroupTypeInteractor";
import { IIndexType } from "../model/IndexType";
import { IIndexTypeCategory } from "../model/IndexTypeCategory";
import { IIndexTypeGroup } from "../model/IndexTypeGroup";
import GroupTreePresentationModel, {
  TGroupTreePresentationModel
} from "./model/GroupTreePresentationModel";

export interface TIndexTypeGroupPresenter extends TLoadingAwarePresenter {
  indexTypeGroups: TGroupTreePresentationModel;
  categories: IIndexTypeCategory[];
  categoryId: number;
  indexTypes: IIndexType[];
  currentGroupId: number;
  currentGroupIndexTypeIds: number[];
  currentGroupChildrenGroupIds: number[];
  createModalVisible: boolean;
  assignItemsModalVisible: boolean;
  createItemModalVisible: boolean;
  assignGroupsModalVisible: boolean;
  category?: IIndexTypeCategory;
}
export interface IIndexTypeGroupPresenter
  extends TIndexTypeGroupPresenter,
    TPresentable {
  loadAllIndexTypeGroups(indexTypeGroups: IIndexTypeGroup[]): void;
  createIndexTypeGroup(indexTypeGroup: IIndexTypeGroup): Promise<boolean>;
  loadReferenceValues(
    categories: IIndexTypeCategory[],
    indexTypes: IIndexType[]
  ): void;
  onChangeCreateModalVisible(value: boolean): void;
  onChangeAssignItemsModalVisible(value: boolean, groupId: number): void;
  onChangeCreateItemModalVisible(value: boolean): void;
  onChangeAssignGroupsModalVisible(value: boolean, groupId: number): void;
  assignItems(values: number[]): void;
  assignGroups(values: number[]): void;
  createIndexType(indexType: IIndexType): Promise<boolean>;
  finishAssigningItems(): void;
  finishAssigningGroups(): void;
  showGroupDetails(id: number): void;
  updateIndexGroupType(indexTypeGroup: IIndexTypeGroup): void;
  deleteIndexGroupType(indexTypeGroup: IIndexTypeGroup): void;
}
const defaultState: TIndexTypeGroupPresenter = {
  indexTypeGroups: {} as TGroupTreePresentationModel,
  categories: [],
  indexTypes: [],
  createModalVisible: false,
  assignItemsModalVisible: false,
  createItemModalVisible: false,
  assignGroupsModalVisible: false,
  currentGroupId: NaN,
  currentGroupIndexTypeIds: [],
  currentGroupChildrenGroupIds: [],
  categoryId: 0
};
const IndexTypeGroupPresenter = withStore<
  IIndexTypeGroupPresenter,
  TIndexTypeGroupPresenter
>(({ application, store, loader, translate }): IIndexTypeGroupPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TIndexTypeGroupPresenter>();
  const loadReferenceValues = (
    categories: IIndexTypeCategory[],
    indexTypes: IIndexType[]
  ) => {
    const { categoryId } = _store.getState<TIndexTypeGroupPresenter>();
    let category = categories?.find((item) => item.id === categoryId);
    _store.update({
      categories,
      indexTypes,
      category,
      breadCrumb: {
        id: () => category?.name
      }
    });
  };
  loader.start("indexTypeGroupsLoader");
  const loadAllIndexTypeGroups = (indexTypeGroups: IIndexTypeGroup[]) => {
    const { categories } = _store.getState<TIndexTypeGroupPresenter>();

    let indexTypeGroupList = GroupTreePresentationModel(
      indexTypeGroups,
      categories
    );
    _store.update({
      indexTypeGroups: indexTypeGroupList
    });
    loader.stop("indexTypeGroupsLoader");
  };
  const getAllIndexTypeGroups = async () => {
    const { categoryId } = _store.getState<TIndexTypeGroupPresenter>();
    let indexTypeGroups = await _application.container
      .resolve<GetAllIndexTypeGroupsForCategoryInteractor>(
        "getAllIndexTypeGroupsForCategory"
      )
      .execute(categoryId);
    loadAllIndexTypeGroups(indexTypeGroups);
  };

  const createIndexTypeGroup = async (indexTypeGroup: IIndexTypeGroup) => {
    loader.start("indexTypeGroupsLoader");
    await _application.container
      .resolve<CreateIndexTypeGroupInteractor>("createIndexTypeGroup")
      .execute(indexTypeGroup);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.index_type.create_success_message"));
    loader.stop("indexTypeGroupsLoader");
    await getAllIndexTypeGroups();
    return true;
  };

  const updateIndexGroupType = async (indexTypeGroup: IIndexTypeGroup) => {
    loader.start("indexTypeGroupsLoader");
    await _application.container
      .resolve<UpdateIndexGroupTypeInteractor>("updateIndexGroupType")
      .execute(indexTypeGroup);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.index_type.create_success_message"));
    loader.stop("indexTypeGroupsLoader");
    await getAllIndexTypeGroups();
  };

  const deleteIndexGroupType = async (indexTypeGroup: IIndexTypeGroup) => {
    loader.start("indexTypeGroupsLoader");
    await _application.container
      .resolve<DeleteIndexGroupTypeInteractor>("deleteIndexGroupType")
      .execute(indexTypeGroup);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.index_type.create_success_message"));
    loader.stop("indexTypeGroupsLoader");
    await getAllIndexTypeGroups();
  };

  const createIndexType = async (indexType: IIndexType) => {
    loader.start("indexTypeGroupsLoader");

    let type = await _application.container
      .resolve<CreateIndexTypeInteractor>("createIndexType")
      .execute(indexType);
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.index_type.create_success_message"));
    loader.stop("indexTypeGroupsLoader");
    const {
      currentGroupIndexTypeIds
    } = _store.getState<TIndexTypeGroupPresenter>();
    _store.update({
      currentGroupIndexTypeIds: [...currentGroupIndexTypeIds, type.id]
    });
    await getAllIndexTypes();
    return true;
  };
  loader.start("indexTypeLoader");
  const loadAllIndexTypes = (indexTypes: IIndexType[]) => {
    _store.update({
      indexTypes
    });
    onChangeCreateItemModalVisible(false);
    loader.stop("indexTypeLoader");
  };
  const getAllIndexTypes = async () => {
    let indexTypes = await _application.container
      .resolve<GetAllIndexTypesInteractor>("getAllIndexTypes")
      .execute();
    loadAllIndexTypes(indexTypes);
  };
  const getGroupIndexTypes = async (groupId: number) => {
    let indexTypes = await _application.container
      .resolve<GetAllGroupItemsInteractor>("getAllGroupItems")
      .execute(groupId);
    _store.update({
      currentGroupIndexTypeIds: indexTypes.map((item) => item.id)
    });
  };
  const getAllGroupChildGroups = async (groupId: number) => {
    let groups = await _application.container
      .resolve<GetAllGroupChildGroupsInteractor>("getAllGroupChildGroups")
      .execute(groupId);
    _store.update({
      currentGroupChildrenGroupIds: groups.map((item) => item.id)
    });
  };

  const onChangeCreateModalVisible = (value: boolean) => {
    _store.update({ createModalVisible: value });
  };
  const onChangeAssignItemsModalVisible = async (
    value: boolean,
    groupId: number
  ) => {
    _store.update({ assignItemsModalVisible: value, currentGroupId: groupId });
    value && (await getGroupIndexTypes(groupId));
  };
  const onChangeAssignGroupsModalVisible = async (
    value: boolean,
    groupId: number
  ) => {
    _store.update({
      assignGroupsModalVisible: value,
      currentGroupId: groupId
    });
    value && (await getAllGroupChildGroups(groupId));
  };
  const onChangeCreateItemModalVisible = (value: boolean) => {
    _store.update({ createItemModalVisible: value });
  };
  const assignItems = (values: number[]) => {
    _store.update({ currentGroupIndexTypeIds: values });
  };
  const finishAssigningItems = async () => {
    const {
      currentGroupId,
      currentGroupIndexTypeIds
    } = _store.getState<TIndexTypeGroupPresenter>();
    await _application.container
      .resolve<AssignGroupItemsInteractor>("assignGroupItems")
      .execute(currentGroupId, currentGroupIndexTypeIds);
    _store.update({ currentGroupIndexTypeIds: [] });
    onChangeAssignItemsModalVisible(false, NaN);
  };
  const assignGroups = (values: number[]) => {
    _store.update({ currentGroupChildrenGroupIds: values });
  };
  const finishAssigningGroups = async () => {
    const {
      currentGroupId,
      currentGroupChildrenGroupIds
    } = _store.getState<TIndexTypeGroupPresenter>();
    await _application.container
      .resolve<AssignGroupChildrenGroupsInteractor>("assignGroupChildrenGroups")
      .execute(currentGroupId, currentGroupChildrenGroupIds);
    _store.update({ currentGroupChildrenGroupIds: [] });
    onChangeAssignGroupsModalVisible(false, NaN);
  };

  const showGroupDetails = (id: number) => {
    const { categoryId } = _store.getState<TIndexTypeGroupPresenter>();
    return _application.navigator.push({
      pathname: `/index-type-categories/${categoryId}/groups/${id}`
    });
  };
  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadAllIndexTypeGroups,
    createIndexTypeGroup,
    loadReferenceValues,
    onChangeCreateModalVisible,
    onChangeAssignItemsModalVisible,
    assignItems,
    onChangeAssignGroupsModalVisible,
    onChangeCreateItemModalVisible,
    assignGroups,
    createIndexType,
    finishAssigningItems,
    finishAssigningGroups,
    showGroupDetails,
    updateIndexGroupType,
    deleteIndexGroupType
  };
}, defaultState);
export default IndexTypeGroupPresenter;
